import { useEffect, useRef, useState } from 'react'
import { LayoutRectangle, Platform, View } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { animationConfig } from '@ui/elements/Segmented/types'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'

import LabelItem from './Label'
import { GuideLine, guidelines } from './helpers'
import { Labels as LablesType } from '../types'
import { topbar } from '@ui/theme/values'
import { BlurView } from 'expo-blur'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'

const padding = 16
const gap = 8 + 2 * padding
const borderRadius = 16
const isWeb = Platform.OS === 'web'

type Element = LayoutRectangle
type Elements = Record<number, Element>

interface Props {
  active: number
  labels: LablesType
  isActive: boolean
}

export default function Labels({ active, labels, isActive }: Props) {
  const [elements, setElements] = useState<Elements>({})
  const isInitialized = useRef(false)

  const { width: screenWidth } = useMaxWindowDimensions()

  const animation = useSharedValue(0)
  const animatedIndicatorWidth = useSharedValue(elements[active]?.width || 0)
  const animatedIndicatorOpacity = useSharedValue(labels[active] ? 1 : 0)
  const animatedCenter = useDerivedValue(() => {
    const indexes = labels.map((o, i) => {
      'worklet'
      return i
    })

    if (!indexes.length) return 0

    const centers = labels.map((o, i) => {
      'worklet'
      return (elements[i]?.x ?? 0) + (elements[i]?.width ?? 0) / 2
    })

    if (indexes.length === 1) return centers[0] ?? 0

    const center = interpolate(animation.value, indexes, centers)
    return center
  })

  const styleIndicator = useAnimatedStyle(() => {
    const screenCenter = screenWidth / 2
    const indicatorWidth = animatedIndicatorWidth.value + 2 * padding
    return {
      width: indicatorWidth,
      left: screenCenter - indicatorWidth / 2,
      opacity: animatedIndicatorOpacity.value,
    }
  })

  const styleContainer = useAnimatedStyle(() => {
    const screenCenter = screenWidth / 2
    const offset = screenCenter - animatedCenter.value

    return {
      left: offset,
    }
  })

  useEffect(() => {
    if (!elements) return

    const el = elements[active]
    if (el) {
      animatedIndicatorWidth.set(
        animatedIndicatorWidth.value !== 0 && isInitialized.current
          ? withSpring(el.width, animationConfig)
          : el.width
      )
    }

    // Animate opacity based on label truthiness
    const targetOpacity = labels[active] ? 1 : 0
    animatedIndicatorOpacity.set(
      isInitialized.current
        ? withSpring(targetOpacity, animationConfig)
        : targetOpacity
    )

    if (!isInitialized.current) {
      isInitialized.current = true
      animation.set(active)
      return
    }

    animation.set(withSpring(active, animationConfig))
  }, [
    active,
    labels,
    elements,
    animatedIndicatorWidth,
    animation,
    animatedIndicatorOpacity,
    isActive,
  ])

  if (!(labels?.length > 0)) return null

  const indicator = (
    <Animated.View
      className="absolute overflow-hidden"
      style={[
        {
          top: 32,
          height: 32,
          borderRadius,
        },
        styleIndicator,
      ]}
    >
      <BlurView
        tint="prominent"
        intensity={40}
        style={{ width: '100%', height: '100%', borderRadius }}
      />
      <Gradient
        colors={[colors.text10, colors.text10]}
        start={[0, 0]}
        end={[1, 1]}
        style={{ borderRadius }}
      />
      {guidelines && <GuideLine className="w-[2px] bg-amber-500" />}
    </Animated.View>
  )

  return (
    <>
      {guidelines && <GuideLine className="w-[1px] bg-green-500" />}
      {!isWeb && (
        <View
          className="absolute inset-0 flex w-full items-center justify-center"
          style={{ height: topbar.height }}
        >
          {indicator}
        </View>
      )}
      {isWeb && indicator}
      <Animated.View
        className="absolute items-center justify-center"
        style={[styleContainer]}
      >
        <View
          className="flex flex-row items-center justify-center"
          style={{ gap }}
        >
          {labels.map((label, index) => {
            return (
              <LabelItem
                key={index}
                label={label}
                index={index}
                active={active}
                onLayout={(event) => {
                  const { layout } = event.nativeEvent
                  setElements((prev) => ({
                    ...prev,
                    [index]: layout,
                  }))
                }}
              />
            )
          })}
        </View>
      </Animated.View>
    </>
  )
}
